import { useDispatch } from "react-redux";
import { type Config, Cta, TokenHistoryLabel } from "kadow-jetpack-model";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import React, { useMemo } from "react";
import { FieldArray, Form, Formik } from "formik";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Text from "../../../designsystem/typography/KC_Text/KC_Text";
import "./styles.scss";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../designsystem/KC_Button/KC_Button";
import KC_Separator from "../../../designsystem/KC_Separator/KC_Separator";
import {
  KC_Sizes,
  KC_Text_Sizes,
  KC_Weights
} from "../../../designsystem/ds_commons";
import { type StepProps } from "../JetpackOnboarding";
import { tt } from "../../../utils/i18n/i18n";
import {
  buildListOfAcquisitionCTAsToDisplay,
  buildListOfRegularCTAsToDisplay
} from "../../../utils/helpers/checkCtas";
import UpsellCTAs from "../../../components/UpsellCTAs/UpsellCTAs";
import CTASelectedCard from "../../19_JetpackConfig/components/CallToAction/module/CTASelectedCard";

export default function CTAsSelectorOnboarding(props: StepProps) {
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const regularCTAsToDisplay = useMemo<TokenHistoryLabel[]>(() => {
    return buildListOfRegularCTAsToDisplay(accountToDisplay, jetpackConfig);
  }, [accountToDisplay, jetpackConfig]);

  const acquisitionCTAsToDisplay = useMemo<TokenHistoryLabel[]>(() => {
    return buildListOfAcquisitionCTAsToDisplay(accountToDisplay, jetpackConfig);
  }, [accountToDisplay, jetpackConfig]);

  const handleSubmit = (values: Config) => {
    dispatch(
      updateJetpackConfigThunk(
        values,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
    props.nextStep(values);
  };

  if (!jetpackConfig?.ctas) {
    return null;
  }

  return (
    <div id="cta-selector">
      <div className="cta-selector-title">
        <KC_Heading3
          text={tt("jp_onboarding_subtitle_step_cta")}
          className="display-lg"
        />
        <KC_Text
          size={KC_Text_Sizes.lg}
          weight={KC_Weights.reg}
          text={tt("jp_onboarding_description_step_cta")}
        />
        <KC_Separator size={KC_Sizes.sm} />
      </div>
      <Formik<Config>
        initialValues={jetpackConfig}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue }) => {
          return (
            <Form>
              <FieldArray name="ctas">
                {() => (
                  <div>
                    {values.ctas
                      .filter((cta) => cta.id === TokenHistoryLabel.Google)
                      .map((cta: Cta, idx: number) => {
                        return (
                          <CTASelectedCard
                            cta={cta}
                            idx={idx}
                            onToggle={() => {}}
                            setFieldValue={setFieldValue}
                            config={values}
                            errors={errors}
                          />
                        );
                      })}
                  </div>
                )}
              </FieldArray>
              <KC_Separator size={KC_Sizes.sm} className="d-none d-md-flex" />
              <KC_Separator size={KC_Sizes.sm} className="d-none d-md-flex" />
              <div className="d-none d-md-flex">
                <KC_Button
                  className="submit-btn d-flex"
                  text={tt("jp_next_btn_text")}
                  type={KC_ButtonType.Submit}
                  size={KC_ButtonSize.Giant}
                />
              </div>
              <KC_Separator size={KC_Sizes.sm} className="d-none d-md-flex" />
              <KC_Separator size={KC_Sizes.m} />
              {!accountToDisplay?.saasFeatures?.ip && (
                <UpsellCTAs
                  config={jetpackConfig}
                  regularCTAsToDisplay={regularCTAsToDisplay}
                  acquisitionCTAsToDisplay={acquisitionCTAsToDisplay}
                />
              )}
              <KC_Separator size={KC_Sizes.sm} />
              <KC_Separator size={KC_Sizes.sm} />
              <div
                className="d-flex flex-column d-md-none"
                style={{
                  bottom: 16
                }}
              >
                <KC_Button
                  id="submit-btn"
                  className="submit-btn d-flex"
                  text={tt("jp_next_btn_text")}
                  type={KC_ButtonType.Submit}
                  size={KC_ButtonSize.Giant}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
